import React from 'react'
import { useWindowDimensions } from 'react-native'
import { BottomTabNavigationOptions, createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useSelector } from 'react-redux'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { StyleService, useStyleSheet } from '@src/style/service'
import {
  NutritionistHubContainer,
  InsightsScreen,
  HomeScreen,
  PlanScreen,
  NoraScreen,
} from '@src/screens'
import { IconName } from '@components/base'
import { TabBarIcon } from '@src/navigation/components/TabBarIcon'
import { TabBarLabel } from '@src/navigation/components/TabBarLabel'
import { useNotifications } from '@src/components/notifications/utils/hooks'
import { MobileAppFeature, NotificationEngineScreen } from '@src/types'
import { Analytics, CustomEventTypes } from '@config'
import { notificationInboxUnreadCountSelector } from '@src/selectors/notifications'
import { getCoachDescriptor, Storage, User } from '@src/utils'
import { useStorageValue } from '@src/utils/storage'
import { userSelector } from '@src/selectors/app'
import { uiFlagsSelector } from '@src/selectors/uiFlags'
import { TabParamList } from '../types'
import { getTabBarVisibilityStyle } from './tabs.navigator.utils'

const customNavigationOptions = (params: {
  label: string
  iconName: IconName
  redDot?: boolean
}) => (): BottomTabNavigationOptions => {
  const { label, iconName, redDot = false } = params

  return {
    tabBarIcon: ({ focused }) => (
      <TabBarIcon focused={focused} iconName={iconName} redDot={redDot} />
    ),
    tabBarLabel: ({ focused }) => <TabBarLabel focused={focused} name={label} />,
    tabBarLabelPosition: 'below-icon',
    title: label,
    tabBarButtonTestID: `TabBar/${label}`,
    headerShown: false,
  }
}

// 8 icon top margin + 24 icon + 4 label bottom margin; the label line (20) scales separately
const TAB_BAR_FIXED_CONTENT_HEIGHT = 36
const TAB_BAR_LABEL_LINE_HEIGHT = 20

export const useTabBarHeight = () => {
  const { fontScale } = useWindowDimensions()
  // never below the default 56 so touch targets don't shrink with small system fonts
  return (
    TAB_BAR_FIXED_CONTENT_HEIGHT + Math.ceil(TAB_BAR_LABEL_LINE_HEIGHT * Math.max(fontScale, 1))
  )
}

const Tab = createBottomTabNavigator<TabParamList>()

export const TabNavigator = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const tabBarHeight = useTabBarHeight()
  const user = useSelector(userSelector)
  const uiFlags = useSelector(uiFlagsSelector)
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const inboxUnreadCount = useSelector(notificationInboxUnreadCountSelector)

  const insightsNotifications = useNotifications(NotificationEngineScreen.Insights)
  const homeNotifications = useNotifications(NotificationEngineScreen.Home)
  const insightsRedDot = notificationInboxEnabled ? false : insightsNotifications.length !== 0
  const homeRedDot = notificationInboxEnabled
    ? inboxUnreadCount !== 0
    : homeNotifications.length !== 0
  const [nutritionistHubRedDot] = useStorageValue(Storage.NUTRITIONIST_HUB_RED_DOT)

  const hasHomeInitialScreen = User.hasFeature(MobileAppFeature.HomeInitialScreen)

  const initialRouteName = hasHomeInitialScreen ? 'Home' : 'NutritionistHub'
  const coachDescriptor = getCoachDescriptor(user)

  return (
    <Tab.Navigator
      initialRouteName={initialRouteName}
      screenOptions={({ route }) => ({
        tabBarStyle: [
          styles.tabBar,
          { height: tabBarHeight + insets.bottom },
          getTabBarVisibilityStyle(!!uiFlags.showNoraHomeTutorial, route.name),
        ],
      })}
    >
      <Tab.Screen
        name="Home"
        component={HomeScreen}
        options={customNavigationOptions({
          label: 'Home',
          iconName: 'house-simple',
          redDot: homeRedDot,
        })}
      />
      <Tab.Screen
        name="Insights"
        component={InsightsScreen}
        options={customNavigationOptions({
          label: 'Trends',
          iconName: 'trend-up',
          redDot: insightsRedDot,
        })}
      />
      <Tab.Screen
        name="Plan"
        component={PlanScreen}
        options={customNavigationOptions({
          label: 'Plan',
          iconName: 'notepad',
        })}
      />
      <Tab.Screen
        name="Nora"
        component={NoraScreen}
        options={customNavigationOptions({
          label: 'Nora',
          iconName: 'sparkle',
        })}
      />
      <Tab.Screen
        name="NutritionistHub"
        component={NutritionistHubContainer}
        listeners={{
          tabPress: () => Analytics.track(CustomEventTypes.BaseBarNutritionistHubTapped),
        }}
        options={customNavigationOptions({
          label: coachDescriptor.capitalized.shortHubTitle.toString(),
          iconName: 'users',
          redDot: !!nutritionistHubRedDot,
        })}
      />
    </Tab.Navigator>
  )
}

const themedStyle = StyleService.create({
  tabBar: {
    backgroundColor: 'theme.background',
    borderTopColor: 'theme.surface.base1',
    borderTopWidth: 1,
  },
})
